import { ChangeDetectorRef, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UserService } from '../../services/user.service';
import { DeviceType, getDeviceType } from 'src/app/Core/utils/deviceUtils';
import { ViewChildren, QueryList } from '@angular/core';
import { ChartComponent } from '../chart/chart.component';
import { Router } from '@angular/router';

interface Location {
  id: string;
  label: string;
}

interface Device {
  serialnumber: string;
  id: string;
  location: Location;
  model: string;
  announcement: string;
  label: string;
  status: string;
  selected: boolean;
  color: string;
}

const IaqDeviceTypes = [
  DeviceType.SmokeIAQOnly,
  DeviceType.SmokeCOIAQ,
  DeviceType.COIAQApollo,
  DeviceType.AthenaCOIAQ,
];

@Component({
  selector: 'app-iaq-charts',
  templateUrl: './iaq-charts.component.html',
  styleUrls: ['./iaq-charts.component.css']
})
export class IaqChartsComponent {
  selectedMeasure = 'temperature';
  selectedMeasuresList: string[] = [];
  selectedDuration: string | null;
  selectedInterval = '1h';
  deviceId!: string;
  locationId!: string;
  locationLabel!: string;
  datasets = [];
  private usedIndexes = new Set<number>();
  userID: string = '';
  iaqDevices: Device[] = [];
  enteredStartDate: Date | null = null;
  enteredEndDate: Date | null = null;
  formattedStartDate: string | null;
  formattedEndDate: string | null;
  minDate: Date = new Date(2000, 0, 1);
  selectedDeviceIDs: number[] = [];
  updateCharts = false;

  private readonly unitMappings: Record<string, string> = {
    temperature: 'C',
    tvoc: 'ppb',
    humidity: '%',
  };

  @ViewChildren(ChartComponent) chartComponents!: QueryList<ChartComponent>;

  constructor(
    private dialogRef: MatDialogRef<IaqChartsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { locationID: string, userID: string, locationLabel: string },
    private userService: UserService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.locationId = this.data.locationID;
    this.userID = this.data.userID;
    this.locationLabel = this.data.locationLabel;
    this.fetchUserDevices();
  }

  openFullscreen(chart: any) {
    this.router.navigate(['/chart-fullscreen'], {
      state: {
        chartData: chart.data,
        chartOptions: chart.options,
        title: chart.title
      }
    });
  }

  fetchUserDevices(): void {
    this.userService.getDevices(this.userID).subscribe((data) => {
      this.iaqDevices = data.filter((device: any) => (IaqDeviceTypes.includes(getDeviceType(device)) && device.location.label === this.locationLabel))
        .map((device: any) => ({
          ...device,
          selected: false,
          color: this.getRandomColor(),
        }));
    });
  }

  toggleSelection(index: number, event: MouseEvent) {
    event.stopPropagation(); // prevent double triggering
    if (this.selectedDuration != null || (this.enteredStartDate != null && this.enteredEndDate != null)) {
      this.iaqDevices[index].selected = !this.iaqDevices[index].selected;

      this.selectedDeviceIDs = this.iaqDevices
        .filter(device => device.selected)
        .map(device => Number(device.id));
    } else {
      alert('Either Select 1D, 7D, 30D or Start Date and End Date ')
    }
  }

  distinctColors: string[] = [
    '#8B0000', // dark red
    '#B22222', // firebrick
    '#8B4513', // saddle brown
    '#A0522D', // sienna
    '#556B2F', // dark olive
    '#006400', // dark green
    '#2E8B57', // sea green
    '#008B8B', // dark cyan
    '#00688B', // deep teal blue
    '#00008B', // dark blue
    '#191970', // midnight blue
    '#4B0082', // indigo
    '#483D8B', // dark slate blue
    '#800080', // purple
    '#8B008B', // dark magenta
    '#C71585', // medium violet red
    '#8B3A3A', // brownish red
    '#2F4F4F', // dark slate gray
    '#3B3B3B', // charcoal
    '#1C1C1C'  // near black
  ];

  getRandomColor(): string {
    if (this.usedIndexes.size >= this.distinctColors.length) {
      const index = Math.floor(Math.random() * this.distinctColors.length);
      return this.distinctColors[index];
    }

    let index: number;
    do {
      index = Math.floor(Math.random() * this.distinctColors.length);
    } while (this.usedIndexes.has(index));

    this.usedIndexes.add(index);
    return this.distinctColors[index];
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  selectMeasure(selected: string[]) {
    this.selectedMeasuresList = selected;
    this.updateCharts = false;
    setTimeout(() => {
      this.updateCharts = true;
    }, 0);
  }

  selectAgoDuration(range: string) {
    this.selectedDuration = range;
    this.enteredStartDate = null;
    this.enteredEndDate = null;
  }

  validateDateRange(): void {
    if (this.enteredStartDate && this.enteredEndDate) {
      this.enteredStartDate = this.setStartOfDay(this.enteredStartDate);
      this.enteredEndDate = this.setEndOfDay(this.enteredEndDate);

      const dayDifference = this.calculateDayDifference(this.enteredStartDate, this.enteredEndDate);
      if (dayDifference > 30) {
        const newEndDate = new Date(this.enteredStartDate);
        newEndDate.setDate(newEndDate.getDate() + 29);
        this.enteredEndDate = newEndDate;
        alert("Maximum duration allowed is 30 days only");
      }
      this.formattedStartDate = this.formatDateInput(this.enteredStartDate);
      this.formattedEndDate = this.formatDateInput(this.enteredEndDate);
      this.selectedDuration = null;
    }
  }

  calculateDayDifference(startDate: Date, endDate: Date): number {
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  }

  setStartOfDay(date: Date): Date {
    const newDate = new Date(date);
    newDate.setHours(0, 0, 0, 0);
    return newDate;
  }

  setEndOfDay(date: Date): Date {
    const newDate = new Date(date);
    newDate.setHours(23, 59, 59, 999);
    return newDate;
  }

  private formatDateInput(date: Date): string {
    const year = date.getFullYear();
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);
    const hours = ('0' + date.getHours()).slice(-2);
    const minutes = ('0' + date.getMinutes()).slice(-2);
    const seconds = String(date.getUTCSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`;
  }

  async exportAll() {
    if (!this.chartComponents?.length) return;
    await this.exportAllChartsImage();
    // small Safari breathing gap
    await new Promise(r => setTimeout(r, 300));
    this.exportAllChartsCSV();
  }

  exportAllChartsImage(): Promise<void> {
    return new Promise((resolve) => {
      const charts = this.chartComponents?.toArray() ?? [];
      if (!charts.length) {
        resolve();
        return;
      }

      const images = charts
        .map(c => c.getChartImage())
        .filter(img => !!img) as string[];

      if (!images.length) {
        resolve();
        return;
      }

      const imageElements: HTMLImageElement[] = [];
      let loaded = 0;

      images.forEach(src => {
        const img = new Image();
        img.src = src;

        img.onload = () => {
          loaded++;
          imageElements.push(img);

          if (loaded === images.length) {
            this.downloadCombinedImage(imageElements);
            resolve(); //CRITICAL for Safari
          }
        };
      });
    });
  }

  private downloadCombinedImage(images: HTMLImageElement[]) {
    const width = Math.max(...images.map(i => i.width));
    const height = images.reduce((sum, img) => sum + img.height, 0);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d')!;

    let y = 0;
    images.forEach(img => {
      ctx.drawImage(img, 0, y);
      y += img.height;
    });

    const link = document.createElement('a');
    link.download = 'iaq-charts.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
  }

  exportAllChartsCSV() {
    const charts = this.chartComponents?.toArray() ?? [];
    if (!charts.length) return;

    let csv = '';

    charts.forEach((chart, index) => {
      const data = chart.getChartCSV();
      if (!data?.header || !data?.rows) return;

      csv += `Chart ${chart.selectedMeasure.charAt(0).toUpperCase() + chart.selectedMeasure.slice(1)}\n`;
      csv += data.header.map(h => `"${h}"`).join(',') + '\n';

      data.rows.forEach(row => {
        csv += row.map(v => `"${v}"`).join(',') + '\n';
      });

      csv += '\n\n';
    });

    const filename = `iaq-charts-data.csv`;

    //Safari browser detection
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    console.log("isSafari:", isSafari);

    if (isSafari) {
      //Safari-safe method (Data URL)
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const reader = new FileReader();

      reader.onload = () => {
        const link = document.createElement('a');
        link.href = reader.result as string;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      };

      reader.readAsDataURL(blob);

    } else {
      //Standard method (Chrome, Edge, Firefox)
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
    }
  }

  openChartFullscreen(chartData: any) {
    const storageKey = 'chart-data';
    sessionStorage.setItem(storageKey, JSON.stringify(chartData));

    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/chart-fullscreen'], {
        queryParams: { key: storageKey }
      })
    );

    window.open(url, '_blank');
  }
}
